<script setup lang="ts">
import { computed, onMounted } from "vue";
import "@maersk-global/mds-components-core/mc-icon";
import "@maersk-global/mds-components-core/mc-button";
import AppSidebarNav from "./AppSidebarNav.vue";
import { IsceSavedWorkspaceList } from "@/features/workspaces";
import { Avatar } from "@/components/Avatar";
import { getInitials } from "@/utils/avatar";
import { useWorkspaceStore } from "@/store";
import { storeToRefs } from "pinia";
import { useSidebarState } from "@/compositions/useSidebarState";
import { useI18n } from "vue-i18n";
import type { NavigationLink } from "@/components/SideNavigation";

defineProps<{
  links: NavigationLink[];
  showWorkspaceList?: boolean;
  showNewWorkspaceButton?: boolean;
}>();

const emit = defineEmits<{
  newWorkspaceClick: [];
}>();

const { t, locale } = useI18n();
const { isCollapsed } = useSidebarState();

const isRtl = computed(() => locale.value === "ar");

// Fetch is owned here (sidebar is always mounted) instead of being tied to
// SavedWorkspaceList, which only mounts in the expanded state. This keeps the
// store populated regardless of collapse state so the collapsed avatars always
// have data, and avoids the destructive re-fetch that wiped the list when
// SavedWorkspaceList re-mounted on expand. getWorkspaces() is idempotent
// (guarded by isInitialized in the store).
const workspaceStore = useWorkspaceStore();
const { workspaceTable } = storeToRefs(workspaceStore);
const workspaces = computed(() => workspaceTable.value?.userFilters ?? []);

onMounted(() => {
  workspaceStore.getWorkspaces();
});
</script>

<template>
  <aside
    id="app-sidebar"
    :class="['app-sidebar', { 'app-sidebar--collapsed': isCollapsed }]"
    :dir="isRtl ? 'rtl' : 'ltr'"
    data-test="app-sidebar/root"
  >
    <div class="app-sidebar__nav">
      <app-sidebar-nav :links="links" />
    </div>

    <div class="app-sidebar__divider" />

    <template v-if="!isCollapsed">
      <div class="app-sidebar__section-label">
        <span class="mds-text--x-small-normal app-sidebar__section-text">
          {{ t("SIDEBAR.WORKSPACES") }}
        </span>
      </div>
      <div class="app-sidebar__workspaces">
        <isce-saved-workspace-list />
        <div v-if="showNewWorkspaceButton" class="app-sidebar__new-workspace">
          <mc-button
            data-test="app-sidebar/new-workspace-btn"
            :label="t('SIDEBAR.NEW_WORKSPACE')"
            icon="plus"
            variant="plain"
            width="full-width"
            justifyitems="left"
            appearance="neutral"
            @click="emit('newWorkspaceClick')"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="workspaces.length" class="app-sidebar__avatars" :dir="isRtl ? 'rtl' : 'ltr'">
        <router-link
          v-for="ws in workspaces"
          :key="ws.id"
          :to="ws.routePath + ws.query"
          :aria-label="ws.name"
          class="app-sidebar__avatar-link"
          exact-active-class="app-sidebar__avatar-link--active"
          active-class="app-sidebar__avatar-link--active"
        >
          <Avatar :initials="getInitials(ws.name, locale)" appearance="color-1" :info="ws.name" fit="small" />
        </router-link>
      </div>
    </template>
  </aside>
</template>

<style scoped lang="scss">
@import "@/styles/colors_mds.scss";

.app-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: $wrapper--mds_brand_appearance_neutral_default_background-color;
  border-radius: var(--mds_brand_border_large_radius, 8px);
  overflow: hidden;
  position: relative;

  &__nav {
    padding-block-start: 12px;
    flex-shrink: 0;
  }

  &__divider {
    height: 1px;
    margin: 8px 16px 4px;
    background-color: $wrapper--mds_brand_appearance_neutral_weak_border-color;
    opacity: 0.4;
    flex-shrink: 0;

    .app-sidebar--collapsed & {
      margin-inline: 12px;
    }
  }

  &__section-label {
    padding-inline: 16px;
    padding-block: 4px;
  }

  &__section-text {
    color: $wrapper--mds_brand_appearance_neutral_weak_text-color;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
  }

  &__workspaces {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__new-workspace {
    padding-inline: 8px;

    mc-button::part(button) {
      background-color: transparent;
      border-radius: var(--mds_brand_border_large_radius, 8px);
      padding: 6px 10px;
    }

    mc-button::part(button):hover {
      background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
    }
  }

  /* edge chevron button removed */

  &__avatars {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding-block: 8px;
    width: 100%;
  }

  &__avatar-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    text-decoration: none;
    border-radius: 50%;
    flex-shrink: 0;

    &:focus-visible {
      outline: 2px solid $wrapper--mds_brand_appearance_primary_weak_background-color;
      outline-offset: 2px;
    }

    &--active {
      outline: 2px solid $wrapper--mds_brand_appearance_primary_weak_background-color;
      outline-offset: 2px;
    }
  }
}

@media (max-width: 767px) {
  .app-sidebar {
    border-radius: 0;
    box-shadow: 4px 0 16px var(--mds_brand_appearance_neutral_default_shadow-color, rgba(0, 0, 0, 0.15));
  }
}
@media (prefers-reduced-motion: reduce) {
  /* no edge chevron present */
}
</style>
